import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

type ElementKey = 'H1' | 'H2' | 'H3' | 'Body' | 'Code';

const ELEMENTS: ElementKey[] = ['H1', 'H2', 'H3', 'Body', 'Code'];
const DEFAULT_SIZES: Record<ElementKey, number> = { H1: 36, H2: 28, H3: 22, Body: 16, Code: 14 };
const DEFAULT_WEIGHTS: Record<ElementKey, number> = { H1: 700, H2: 700, H3: 600, Body: 400, Code: 400 };
const DEFAULT_PREVIEWS: Record<ElementKey, string> = {
  H1: 'The quick brown fox',
  H2: 'Jumps over the lazy dog',
  H3: 'Pack my box with five dozen liquor jugs',
  Body: 'The quick brown fox jumps over the lazy dog. Sphinx of black quartz, judge my vow. How vexingly quick daft zebras jump.',
  Code: 'const hello = "world";\nconsole.log(hello);',
};

function FontPicker({ value, onChange, fontFamilies, theme }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = fontFamilies.filter(f =>
    !search || f.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'relative', marginBottom: 6 }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        background: 'transparent',
        border: `1px solid ${open ? theme.accent : theme.borderMedium}`,
        borderRadius: 0,
      }}>
        <input
          type="text"
          value={open ? search : (value || '')}
          placeholder={open ? 'search fonts…' : '— select font —'}
          onFocus={() => { setOpen(true); setSearch(''); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, height: 34, padding: '0 10px',
            background: 'none', border: 'none',
            color: value && !open ? theme.text : theme.textMuted,
            fontSize: 12, fontFamily: 'inherit', outline: 'none', cursor: 'text',
          }}
        />
        <span style={{ padding: '0 10px', color: theme.textGhost, fontSize: 9, flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: theme.bgElevated, border: `1px solid ${theme.borderHover}`,
          borderRadius: 0, boxShadow: theme.shadow,
          maxHeight: 220, overflowY: 'auto', marginTop: 2,
        }}>
          <div
            onMouseDown={() => { onChange(''); setOpen(false); setSearch(''); }}
            style={{ padding: '6px 10px', fontSize: 11, color: theme.textFaint, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >— none —</div>
          {filtered.map(f => (
            <div
              key={f}
              onMouseDown={() => { onChange(f); setOpen(false); setSearch(''); }}
              style={{
                padding: '6px 10px', fontSize: 11, cursor: 'pointer',
                color: f === value ? theme.accent : theme.text,
                fontWeight: f === value ? 600 : 400,
                background: f === value ? theme.accentBg : 'none',
              }}
              onMouseEnter={e => { if (f !== value) e.currentTarget.style.background = theme.bgHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = f === value ? theme.accentBg : 'none'; }}
            >{f}</div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 11, color: theme.textFaint }}>no fonts match</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CSSPlayground() {
  const { theme } = useTheme();

  type Assignment = { family: string; size: number; weight: number };

  const [assignments, setAssignments] = useState<Record<ElementKey, Assignment>>(() => {
    const init = {} as Record<ElementKey, Assignment>;
    for (const el of ELEMENTS) {
      init[el] = { family: '', size: DEFAULT_SIZES[el], weight: DEFAULT_WEIGHTS[el] };
    }
    return init;
  });

  const [previewTexts, setPreviewTexts] = useState<Record<ElementKey, string>>({ ...DEFAULT_PREVIEWS });
  const [allFamilies, setAllFamilies] = useState<string[]>([]);
  const [loadingFonts, setLoadingFonts] = useState(true);
  const [copied, setCopied] = useState(false);

  // Fetch ALL font families by paginating (server caps each page at 100)
  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      const families = new Set<string>();
      let page = 1;
      while (true) {
        const data = await fontsApi.list({ sort: 'name', order: 'ASC', limit: 100, page });
        if (cancelled) return;
        const batch = data.fonts || [];
        batch.forEach(f => { const n = f.family || f.name; if (n) families.add(n); });
        if (batch.length < 100 || page >= (data.pagination?.pages || 1)) break;
        page++;
      }
      setAllFamilies([...families].sort((a, b) => a.localeCompare(b)));
      setLoadingFonts(false);
    }
    fetchAll().catch(() => setLoadingFonts(false));
    return () => { cancelled = true; };
  }, []);

  const setAssignment = (el: ElementKey, key: keyof Assignment, value: string | number) => {
    setAssignments(prev => ({ ...prev, [el]: { ...prev[el], [key]: value } }));
  };

  const setPreviewText = (el, text) => {
    setPreviewTexts(prev => ({ ...prev, [el]: text }));
  };

  const generateCSS = () => {
    let css = '/* FontVault CSS Export */\n\n';
    for (const el of ELEMENTS) {
      const a = assignments[el];
      if (!a.family) continue;
      const selector = el === 'Body' ? 'body' : el === 'Code' ? 'code, pre' : el.toLowerCase();
      css += `${selector} {\n`;
      css += `  font-family: '${a.family}', ${el === 'Code' ? 'monospace' : 'sans-serif'};\n`;
      css += `  font-size: ${a.size}px;\n`;
      css += `  font-weight: ${a.weight};\n`;
      css += `}\n\n`;
    }
    return css;
  };

  const handleExport = () => {
    navigator.clipboard.writeText(generateCSS());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const anyAssigned = ELEMENTS.some(el => assignments[el].family);

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* Left: Controls */}
      <div style={{
        width: 300, flexShrink: 0, borderRight: `1px solid ${theme.border}`,
        padding: '24px 20px', overflowY: 'auto',
      }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: theme.textFaint, paddingBottom: 10, borderBottom: `2px solid ${theme.text}`,
        }}>css playground</div>

        {loadingFonts && (
          <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 12 }}>
            loading fonts…
          </div>
        )}

        {ELEMENTS.map(el => (
          <div key={el} style={{ marginTop: 20 }}>
            <div style={{
              fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: theme.text, marginBottom: 8,
            }}>{el}</div>

            <FontPicker
              value={assignments[el].family}
              onChange={v => setAssignment(el, 'family', v)}
              fontFamilies={allFamilies}
              theme={theme}
            />

            <div style={{ display: 'flex', gap: 6 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.textFaint }}>size</span>
                <input
                  type="number" min={8} max={120} value={assignments[el].size}
                  onChange={e => setAssignment(el, 'size', parseInt(e.target.value) || 16)}
                  style={{
                    width: '100%', height: 30, padding: '0 8px', boxSizing: 'border-box',
                    background: 'transparent', border: `1px solid ${theme.border}`,
                    borderRadius: 0, color: theme.text, fontSize: 12,
                    fontFamily: 'inherit',
                  }}
                />
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.textFaint }}>weight</span>
                <select
                  value={assignments[el].weight}
                  onChange={e => setAssignment(el, 'weight', parseInt(e.target.value))}
                  style={{
                    width: '100%', height: 30, padding: '0 6px', boxSizing: 'border-box',
                    background: 'transparent', border: `1px solid ${theme.border}`,
                    borderRadius: 0, color: theme.text, fontSize: 12,
                    fontFamily: 'inherit', cursor: 'pointer',
                  }}
                >
                  {[100,200,300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
            </div>

            <div style={{ marginTop: 6 }}>
              {el === 'Code' ? (
                <textarea
                  value={previewTexts[el]}
                  onChange={e => setPreviewText(el, e.target.value)}
                  rows={2}
                  placeholder="preview text"
                  style={{
                    width: '100%', padding: '6px 8px',
                    background: 'transparent', border: `1px solid ${theme.border}`,
                    borderRadius: 0, color: theme.text, fontSize: 11,
                    fontFamily: 'monospace', resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <input
                  type="text"
                  value={previewTexts[el]}
                  onChange={e => setPreviewText(el, e.target.value)}
                  placeholder={DEFAULT_PREVIEWS[el]}
                  style={{
                    width: '100%', height: 30, padding: '0 8px',
                    background: 'transparent', border: `1px solid ${theme.border}`,
                    borderRadius: 0, color: theme.text, fontSize: 11,
                    fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 24, borderTop: `2px solid ${theme.text}`, paddingTop: 14, display: 'flex' }}>
          <button
            onClick={handleExport}
            style={{
              flex: 1, height: 40,
              background: theme.fill, border: 'none', color: theme.fillFg,
              fontFamily: 'inherit', fontSize: 13, textTransform: 'lowercase',
              textAlign: 'left', padding: '0 14px', cursor: 'pointer',
            }}
          >{copied ? 'copied.' : 'copy css'}</button>
          <span
            onClick={handleExport}
            style={{
              height: 40, width: 40, background: theme.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: theme.accentFg, fontSize: 12,
            }}
          >▶</span>
        </div>
      </div>

      {/* Right: Live Preview */}
      <div style={{ flex: 1, minWidth: 0, padding: 32, overflowY: 'auto', background: theme.bgSecondary }}>
        <div style={{
          background: theme.bg, border: `1px solid ${theme.border}`, padding: 40, minHeight: 340,
        }}>
          {assignments.H1.family && (
            <h1 style={{
              fontFamily: `'${assignments.H1.family}', sans-serif`,
              fontSize: assignments.H1.size, fontWeight: assignments.H1.weight,
              color: theme.text, lineHeight: 1.15, margin: '0 0 14px',
            }}>{previewTexts.H1 || DEFAULT_PREVIEWS.H1}</h1>
          )}
          {assignments.H2.family && (
            <h2 style={{
              fontFamily: `'${assignments.H2.family}', sans-serif`,
              fontSize: assignments.H2.size, fontWeight: assignments.H2.weight,
              color: theme.text, lineHeight: 1.2, margin: '0 0 10px',
            }}>{previewTexts.H2 || DEFAULT_PREVIEWS.H2}</h2>
          )}
          {assignments.H3.family && (
            <h3 style={{
              fontFamily: `'${assignments.H3.family}', sans-serif`,
              fontSize: assignments.H3.size, fontWeight: assignments.H3.weight,
              color: theme.textSecondary, lineHeight: 1.25, margin: '0 0 14px',
            }}>{previewTexts.H3 || DEFAULT_PREVIEWS.H3}</h3>
          )}
          {assignments.Body.family && (
            <p style={{
              fontFamily: `'${assignments.Body.family}', sans-serif`,
              fontSize: assignments.Body.size, fontWeight: assignments.Body.weight,
              color: theme.textSecondary, lineHeight: 1.55, margin: '0 0 18px', maxWidth: '62ch',
            }}>{previewTexts.Body || DEFAULT_PREVIEWS.Body}</p>
          )}
          {assignments.Code.family && (
            <pre style={{
              fontFamily: `'${assignments.Code.family}', monospace`,
              fontSize: assignments.Code.size,
              background: theme.bgSecondary, border: `1px solid ${theme.border}`, padding: 16,
              color: theme.textSecondary, whiteSpace: 'pre-wrap', margin: 0,
            }}>{previewTexts.Code || DEFAULT_PREVIEWS.Code}</pre>
          )}
          {!anyAssigned && (
            <div style={{ color: theme.textFaint, textAlign: 'center', padding: 40, fontSize: 13 }}>
              select fonts from the left panel to preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
